import type { z } from 'zod';

/**
 * Access token stays in module memory. The refresh token is an HttpOnly cookie, so every
 * call uses credentials: 'include' and retries once after POST /v1/auth/refresh.
 */

let accessToken: string | null = null;
let tenantId: string | null = null;
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

export function setTenantId(id: string | null): void {
  tenantId = id;
}

export function getTenantId(): string | null {
  return tenantId;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getApiBaseUrl(env: Record<string, string | undefined> = import.meta.env): string {
  const raw = env['VITE_API_URL']?.trim();
  if (!raw) return 'http://localhost:5000';
  return raw.replace(/\/+$/, '');
}

export function buildApiUrl(path: string, baseUrl: string = getApiBaseUrl()): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalized}`;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type RawUploadStage = 'uploading' | 'processing';

export interface RawUploadOptions {
  contentType: string;
  timeoutMs: number;
  signal?: AbortSignal;
  onStage?: (stage: RawUploadStage) => void;
  onProgress?: (loaded: number, total: number | null) => void;
  /** Test seam; production uses the browser XMLHttpRequest implementation. */
  xhrFactory?: () => XMLHttpRequest;
}

export async function apiRequest(
  path: string,
  init: RequestInit = {},
  allowRefresh = true,
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body !== undefined && !headers.has('Content-Type'))
    headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  // A caller may name a tenant explicitly (platform admin support view); otherwise the selected one.
  // An explicitly empty header means "platform-wide call": send no tenant at all, so a platform
  // admin browsing a client does not stamp unrelated calls with audited tenant access.
  if (tenantId && !headers.has('X-Tenant-Id')) headers.set('X-Tenant-Id', tenantId);
  if (headers.get('X-Tenant-Id') === '') headers.delete('X-Tenant-Id');
  const response = await fetch(buildApiUrl(path), { ...init, headers, credentials: 'include' });
  if (response.status === 401 && path !== '/v1/auth/login') {
    if (allowRefresh && path !== '/v1/auth/refresh') {
      if (await refreshAccessToken()) return apiRequest(path, init, false);
    }
    expireSession(path !== '/v1/auth/refresh');
  }
  return response;
}

export async function openAuthenticatedPdf(path: string): Promise<void> {
  const pdfWindow = window.open('about:blank', '_blank');
  if (!pdfWindow) {
    throw new Error('The browser blocked the new window. Allow pop-ups and try again.');
  }
  pdfWindow.opener = null;
  try {
    const response = await apiRequest(path);
    if (!response.ok) throw new Error('The original PDF is unavailable.');
    const received = await response.blob();
    const pdf =
      received.type === 'application/pdf'
        ? received
        : new Blob([received], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdf);
    pdfWindow.location.replace(url);
    window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
  } catch (error) {
    pdfWindow.close();
    throw error;
  }
}

export async function uploadRawJson<T>(
  path: string,
  body: ArrayBuffer | Blob,
  options: RawUploadOptions,
): Promise<T> {
  let result = await sendRawUpload(path, body, options);
  if (result.status === 401 && (await refreshAccessToken())) {
    result = await sendRawUpload(path, body, options);
  }
  if (result.status === 401) expireSession();
  if (result.status < 200 || result.status >= 300) {
    throw new ApiError(result.status, errorMessage(result.body, result.status));
  }
  return result.body as T;
}

async function sendRawUpload(
  path: string,
  body: ArrayBuffer | Blob,
  options: RawUploadOptions,
): Promise<{ status: number; body: unknown }> {
  if (options.signal?.aborted) throw new DOMException('Upload aborted', 'AbortError');
  const xhr = options.xhrFactory?.() ?? new XMLHttpRequest();
  options.onStage?.('uploading');
  return await new Promise((resolve, reject) => {
    const abort = () => xhr.abort();
    const cleanup = () => options.signal?.removeEventListener('abort', abort);
    options.signal?.addEventListener('abort', abort, { once: true });
    xhr.open('POST', buildApiUrl(path));
    xhr.withCredentials = true;
    xhr.timeout = options.timeoutMs;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', options.contentType);
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    if (tenantId) xhr.setRequestHeader('X-Tenant-Id', tenantId);
    xhr.upload.addEventListener('progress', (event) => {
      options.onProgress?.(event.loaded, event.lengthComputable ? event.total : null);
    });
    xhr.upload.addEventListener('load', () => options.onStage?.('processing'));
    xhr.addEventListener('load', () => {
      cleanup();
      resolve({ status: xhr.status, body: parseJson(xhr.responseText) });
    });
    xhr.addEventListener('error', () => {
      cleanup();
      reject(new ApiError(0, 'The upload failed because of a network error.'));
    });
    xhr.addEventListener('timeout', () => {
      cleanup();
      reject(new ApiError(408, 'File processing timed out. Try a smaller file.'));
    });
    xhr.addEventListener('abort', () => {
      cleanup();
      reject(new DOMException('Upload aborted', 'AbortError'));
    });
    xhr.send(body);
  });
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshed = await fetch(buildApiUrl('/v1/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
  });
  if (!refreshed.ok) return false;
  const body: unknown = await refreshed.json().catch(() => null);
  if (
    !body ||
    typeof body !== 'object' ||
    !('accessToken' in body) ||
    typeof body.accessToken !== 'string'
  ) {
    return false;
  }
  accessToken = body.accessToken;
  return true;
}

function expireSession(notify = true): void {
  accessToken = null;
  if (notify) onSessionExpired?.();
}

function parseJson(value: string): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function errorMessage(body: unknown, status: number): string {
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error?: { message?: string } }).error?.message ?? status)
    : `HTTP ${status}`;
}

export async function fetchJson<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(buildApiUrl(path), {
    ...init,
    headers: { Accept: 'application/json', ...init?.headers },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok && !(response.status === 503 && body !== null)) {
    throw new ApiError(response.status, `HTTP ${response.status}`);
  }
  return schema.parse(body);
}
